import { useEffect } from "react";
import { X } from "lucide-react";

export { Label, Field, Select, Notice, money, when } from "@/webapp/shared/ui";

export const TONES = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warn: "bg-amber-50 text-amber-700 border-amber-200",
  bad: "bg-red-50 text-red-700 border-red-200",
  info: "bg-blue-50 text-blue-800 border-blue-200",
  mute: "bg-slate-50 text-slate-500 border-slate-200",
};
const RES = { booked: "warn", verified: "info", onsite: "ok", completed: "mute", cancelled: "bad" };
export const resTone = (s) => RES[s] || "mute";
export const SEVERITY = { critical: "bad", warning: "warn", info: "info" };

export const Tag = ({ tone = "mute", children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${TONES[tone]}`}>{children}</span>
);

export const Panel = ({ children, className = "", testid, title, action }) => (
  <section data-testid={testid} className={`rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}>
    {title && (
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
        <h2 className="font-display text-sm font-semibold text-[#0A234A]">{title}</h2>
        {action}
      </header>
    )}
    {children}
  </section>
);

export const PageHead = ({ eyebrow, title, sub, children }) => (
  <div className="fo-rise mb-8 flex flex-wrap items-end justify-between gap-4">
    <div>
      {eyebrow && <p className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FF5722]">{eyebrow}</p>}
      <h1 className="font-display text-3xl font-semibold text-[#0A234A] sm:text-4xl">{title}</h1>
      {sub && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500 md:text-base">{sub}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const Button = ({ children, variant = "default", testid, className = "", ...p }) => {
  const v = {
    primary: "bg-[#FF5722] text-white hover:bg-[#E64A19]",
    navy: "bg-[#0A234A] text-white hover:bg-[#071936]",
    danger: "border border-red-200 bg-white text-red-600 hover:bg-red-50",
    ghost: "text-[#0A234A] hover:bg-slate-100",
    default: "border border-slate-300 bg-white text-[#0A234A] hover:bg-slate-50",
  }[variant];
  return (
    <button data-testid={testid} {...p} className={`inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-[1px] active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5722]/40 disabled:pointer-events-none disabled:opacity-40 ${v} ${className}`}>{children}</button>
  );
};

export const Kpi = ({ label, value, hint, tone, testid, className = "" }) => (
  <div data-testid={testid} className={`rounded-lg border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-2 font-mono text-3xl font-semibold ${tone === "accent" ? "text-[#FF5722]" : tone === "bad" ? "text-red-600" : "text-[#0A234A]"}`}>{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
  </div>
);

export const EmptyState = ({ title, body, children, testid, icon: Icon }) => (
  <div data-testid={testid} className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/70 px-6 py-12 text-center">
    {Icon && <Icon className="mb-3 h-6 w-6 text-slate-300" strokeWidth={1.5} />}
    <p className="font-display text-base font-semibold text-[#0A234A]">{title}</p>
    {body && <p className="mt-1 max-w-md text-sm text-slate-500">{body}</p>}
    {children && <div className="mt-5 flex flex-wrap justify-center gap-2">{children}</div>}
  </div>
);

export const DataTable = ({ head, children, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-left text-sm">
      <thead><tr className="border-b border-slate-200 bg-slate-50/60">{head.map((h, i) => <th key={`${h}-${i}`} className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
      <tbody className="[&>tr]:border-b [&>tr]:border-slate-100 [&>tr]:transition-colors [&>tr:hover]:bg-slate-50">{children}</tbody>
    </table>
  </div>
);

export const Td = ({ children, mono, strong, className = "" }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${mono ? "font-mono text-xs" : ""} ${strong ? "font-semibold text-[#0A234A]" : "text-slate-600"} ${className}`}>{children}</td>
);

export function MiniBars({ series, testid, color = "bg-[#0A234A]" }) {
  const max = Math.max(1, ...series.map((s) => s.value));
  return (
    <div data-testid={testid} className="space-y-2.5">
      {series.map((s) => (
        <div key={s.label} className="grid grid-cols-[7rem_1fr_5.5rem] items-center gap-3 text-xs">
          <span className="truncate font-medium text-slate-600">{s.label}</span>
          <span className="h-2.5 overflow-hidden rounded-sm bg-slate-100"><span className={`block h-full rounded-sm ${color} transition-[width] duration-500`} style={{ width: `${(s.value / max) * 100}%` }} /></span>
          <span className="text-right font-mono font-semibold text-[#0A234A]">{s.display}</span>
        </div>
      ))}
    </div>
  );
}

export function Drawer({ open, onClose, title, children, testid, printable }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid={testid}>
      <button aria-label="Close" data-testid={`${testid}-backdrop`} onClick={onClose} className="fo-noprint absolute inset-0 bg-[#0A234A]/30 backdrop-blur-[2px]" />
      <aside className={`fo-drawer relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl ${printable ? "fo-print-area" : ""}`}>
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="font-display text-lg font-semibold text-[#0A234A]">{title}</h2>
          <button data-testid={`${testid}-close`} onClick={onClose} className="fo-noprint rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#0A234A]"><X className="h-5 w-5" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </aside>
    </div>
  );
}

export const daysUntil = (d) => (d ? Math.ceil((new Date(d).getTime() - new Date(new Date().toISOString().slice(0, 10)).getTime()) / 86400000) : null);

export function ExpiryCell({ date, warn = 30, testid }) {
  if (!date) return <span className="text-slate-300">—</span>;
  const n = daysUntil(date);
  const tone = n < 0 ? "bad" : n <= warn ? "warn" : "ok";
  return <span data-testid={testid} className="inline-flex items-center gap-2"><span className="font-mono text-xs text-slate-600">{date}</span>{tone !== "ok" && <Tag tone={tone}>{n < 0 ? "expired" : `${n}d`}</Tag>}</span>;
}

export function downloadFile(name, text, type = "text/csv") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export const toCsv = (rows, cols) => [cols.map((c) => c[0]).join(","), ...rows.map((r) => cols.map((c) => `"${String(c[1](r) ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
